/**
 * WS-NAV — per-route device capability table (mobile_strategy §2.2).
 *
 * "Which pages exist on a phone" is application logic, not browser magic. This
 * is the one table that answers it; both the router's `<RequireDesktop>` gate
 * and the sidebar's nav rendering read from here, so a route can never drift
 * from the nav entry that points at it.
 *
 * Default is `all` — a page has to *earn* `desktop-only`. The three earners
 * (mobile_strategy §2.2):
 *   - the analysis viewer (OpenSeadragon + Konva, pointer-precision work),
 *   - the pilot upload wizard (an SD card is not a phone input),
 *   - the DataGrid-heavy administration / configuration pages.
 *
 * Deliberately NOT desktop-only: Home, the coming-soon stubs, and the plain
 * asset/project list pages ("simple lists" per the brief) — they degrade to a
 * scrollable grid rather than breaking.
 */
import type { DeviceCapability } from "./types.js";

/**
 * Path prefixes that require a desktop viewport. A prefix matches the exact
 * path or any deeper segment (`/admin/users` matches `/admin/users/:id`).
 */
const DESKTOP_ONLY_PREFIXES: readonly string[] = [
  // Analysis viewer — OpenSeadragon canvas + Konva bbox overlay.
  "/analysis",
  // Pilot upload wizard — reads folders / an SD card off a notebook.
  "/field-data/upload",
  // DataGrid-heavy administration + configuration surfaces.
  "/admin",
  "/settings/limits",
  "/defect-catalogues",
  "/ai-models",
  "/ai-findings-import",
  "/platform-admin",
];

/**
 * TT-03 — exact desktop-only SUFFIXES under `/projects/:id`.
 *
 * `/projects` is a plain list that degrades fine on a phone, but
 * `/projects/:id/time-trackings` is an eight-column comparison table with
 * multi-select and full invoice text, and `/projects/:id/settings` is a pricing
 * form. Neither is a phone job — and a prefix entry above would have taken the
 * whole Projects workspace with it.
 */
const PROJECT_DESKTOP_ONLY_SUFFIXES: readonly string[] = [
  "/time-trackings",
  "/settings",
];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The device capability of `pathname`. Unknown paths default to `all`. */
export function deviceForPath(pathname: string): DeviceCapability {
  if (DESKTOP_ONLY_PREFIXES.some((p) => matchesPrefix(pathname, p))) {
    return "desktop-only";
  }
  if (
    pathname.startsWith("/projects/") &&
    PROJECT_DESKTOP_ONLY_SUFFIXES.some((s) => pathname.endsWith(s))
  ) {
    return "desktop-only";
  }
  return "all";
}

/** True when `pathname` renders on a phone. */
export function isMobileCapable(pathname: string): boolean {
  return deviceForPath(pathname) === "all";
}
